﻿import {isNullOrEmpty} from "@2d-front-monorepo/core";
import {ErrorMessage, Message, RemoteDataStatus, useRemoteData} from "@2d-front-monorepo/design-library";
import {PermitId, Permits} from "@2d-front-monorepo/permits-business";
import {JSX, useEffect} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router";
import InvalidPermitId from "../../components/InvalidPermitId";
import PermitsInlineLoading from "../../components/PermitsInlineLoading";
import {fetchPermit} from "../../hooks/fetchPermit";
import {usePermits} from "../../hooks/usePermits";
import {formatRoute, PrivateRoutes} from "../../routes";

export default function RenewPermit(): JSX.Element {
	const {permitId} = useParams<{ permitId: string }>() as { permitId: PermitId };
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const renewGroup = searchParams.get("group") === "1";
	const permitsState = usePermits();
	const stored = Permits.find(permitsState, permitId);
	// An archived permit is not in the store, so it has to be read back. Doing it HERE and not in
	// the wizard is what makes the renewal preload work: this page always precedes the wizard, and
	// fetchPermit caches, so the wizard's synchronous memo already finds the permit.
	const {data, status, error} = useRemoteData(
		signal => stored != null ? Promise.resolve(stored) : fetchPermit(permitId, signal),
		[permitId, stored]);
	const permit = stored ?? data;
	const parkingType = permit?.parkingType;

	useEffect(() => {
		if (isNullOrEmpty(parkingType)) {
			return;
		}

		const query = "?renewPermitId=" + encodeURIComponent(permitId) + (renewGroup ? "&renewGroup=1" : "");
		navigate(formatRoute(PrivateRoutes.permitsNew, parkingType) + query, {replace: true});
	}, [navigate, parkingType, permitId, renewGroup]);

	if (status === RemoteDataStatus.Loading) {
		return <PermitsInlineLoading/>;
	}

	// A failed read is not a bad id: without this, a 500 or a dropped connection tells the user
	// their permit id is wrong.
	if (status === RemoteDataStatus.Error) {
		return <ErrorMessage error={error}/>;
	}

	if (permit == null) {
		return <InvalidPermitId permitId={permitId}/>;
	}

	if (isNullOrEmpty(parkingType)) {
		return <Message title={{text: `Permit has no parking type. Can't be renewed.`}} type="error"/>;
	}

	return <></>;
}
